import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Calendar, FileText, ListChecks, Upload } from 'lucide-react';
import Modal2, { Modal2Footer, Modal2Button } from '../../../common/Modal2';
import Badge from '../../../common/Badge';
import { useSurveyInfo } from '../hooks/useSurveyInfo';
import { useTakeSurvey } from '../hooks/useTakeSurvey';
import QuestionDisplay from './QuestionDisplay';
import SubmitConfirmationModal from './SubmitConfirmationModal';
import { formatDDMMYYYY } from '../../../../utils/timeUtils';

interface TakeSurveyModalProps {
  surveyId: string;
  onClose: () => void;
  onSubmitted: () => void;
}

type ModalStep = 'info' | 'taking';

const INFO_STATUS: Record<string, { label: string; variant: 'green' | 'blue' | 'gray' }> = {
  active: { label: 'Active', variant: 'green' },
  upcoming: { label: 'Upcoming', variant: 'blue' },
  closed: { label: 'Closed', variant: 'gray' },
};

/** Figma take-survey flow as a modal (dc.html:3758): info step, then
 *  questions one-by-one. Reuses the existing info/take hooks and question
 *  renderer — presentation wrapper only, no logic changes. */
const TakeSurveyModal: React.FC<TakeSurveyModalProps> = ({ surveyId, onClose, onSubmitted }) => {
  const [step, setStep] = useState<ModalStep>('info');
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const { surveyInfo, isLoading: infoLoading, error: infoError } = useSurveyInfo(surveyId);
  const {
    currentQuestion,
    currentIndex,
    totalQuestions,
    answers,
    answeredCount,
    requiredRemaining,
    progress,
    isLoading: takeLoading,
    isSubmitting,
    setAnswer,
    goToNext,
    goToPrevious,
    submitSurvey,
    isFirstQuestion,
    isLastQuestion,
    canSubmit,
  } = useTakeSurvey(surveyId);

  useEffect(() => {
    setStep('info');
    setShowSubmitModal(false);
  }, [surveyId]);

  const handleSubmit = async () => {
    const success = await submitSurvey();
    if (success) {
      setShowSubmitModal(false);
      onSubmitted();
    }
  };

  const title = surveyInfo?.title ?? 'Survey';
  const statusConfig = (surveyInfo && INFO_STATUS[surveyInfo.status]) || INFO_STATUS.active;

  return (
    <>
      <Modal2
        isOpen
        onClose={onClose}
        title={
          <div className="pr-3.5">
            <div className="text-[18px] font-extrabold tracking-[-0.02em] text-[var(--color-text-primary)] leading-tight">
              {infoLoading ? 'Loading…' : title}
            </div>
            {step === 'taking' && !takeLoading && totalQuestions > 0 && (
              <p className="mt-[7px] text-[12px] text-[var(--color-text-tertiary)]">
                Question {currentIndex + 1} of {totalQuestions}
                {surveyInfo?.isConfidential ? ' · anonymous' : ''}
              </p>
            )}
          </div>
        }
        width={560}
        footer={
          step === 'info' ? (
            <Modal2Footer justify="between">
              <Modal2Button variant="secondary" onClick={onClose}>
                Back to My Surveys
              </Modal2Button>
              <Modal2Button
                variant="primary"
                onClick={() => setStep('taking')}
                disabled={infoLoading || !surveyInfo}
              >
                Begin Survey
                <ArrowRight className="w-4 h-4" />
              </Modal2Button>
            </Modal2Footer>
          ) : (
            <Modal2Footer justify="between">
              <Modal2Button
                variant="secondary"
                onClick={goToPrevious}
                disabled={isFirstQuestion || takeLoading}
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Modal2Button>
              {isLastQuestion ? (
                <Modal2Button
                  variant="primary"
                  onClick={() => setShowSubmitModal(true)}
                  disabled={!canSubmit || isSubmitting}
                >
                  <Upload className="w-4 h-4" />
                  Submit Survey
                </Modal2Button>
              ) : (
                <Modal2Button variant="primary" onClick={goToNext} disabled={takeLoading}>
                  Next
                  <ArrowRight className="w-4 h-4" />
                </Modal2Button>
              )}
            </Modal2Footer>
          )
        }
      >
        {step === 'info' ? (
          infoLoading ? (
            <div className="py-8 text-center text-body-sm text-[var(--color-text-tertiary)]">
              Loading survey…
            </div>
          ) : infoError || !surveyInfo ? (
            <div className="text-center py-8">
              <p className="text-body-sm text-[var(--color-text-secondary)] mb-2">
                {infoError || 'Survey not found'}
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant={statusConfig.variant} size="middle">
                  {statusConfig.label}
                </Badge>
                <span className="inline-flex items-center gap-1.5 text-[12px] text-[var(--color-text-tertiary)]">
                  <Calendar className="w-3.5 h-3.5" />
                  Due {formatDDMMYYYY(surveyInfo.dueDate) ?? '—'}
                </span>
                <span className="inline-flex items-center gap-1.5 text-[12px] text-[var(--color-text-tertiary)]">
                  <ListChecks className="w-3.5 h-3.5" />
                  {surveyInfo.totalQuestions} {surveyInfo.totalQuestions === 1 ? 'question' : 'questions'}
                </span>
              </div>

              {surveyInfo.aboutText && (
                <p className="text-body-md text-[var(--color-text-secondary)] leading-relaxed whitespace-pre-line">
                  {surveyInfo.aboutText}
                </p>
              )}

              {surveyInfo.instructions.length > 0 && (
                <div className="rounded-[12px] border border-[var(--color-border-primary)] bg-[var(--color-bg-hover)] p-4">
                  <div className="flex items-center gap-2 mb-2.5">
                    <FileText className="w-4 h-4 text-[var(--color-primary-700)]" />
                    <span className="text-label font-bold text-[var(--color-text-primary)]">Instructions</span>
                  </div>
                  <ul className="space-y-1.5">
                    {surveyInfo.instructions.map((instruction, i) => (
                      <li key={i} className="text-body-sm text-[var(--color-text-secondary)] leading-relaxed">
                        {instruction}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )
        ) : takeLoading || !currentQuestion ? (
          <div className="py-8 text-center text-body-sm text-[var(--color-text-tertiary)]">
            Loading questions…
          </div>
        ) : (
          <div>
            <div
              className="h-1.5 rounded-full bg-[var(--color-bg-subtle)] overflow-hidden mb-5"
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full rounded-full transition-all duration-300 bg-[linear-gradient(135deg,var(--color-primary-700),color-mix(in_srgb,var(--color-primary-700)_72%,transparent))]"
                style={{ width: `${progress}%` }}
              />
            </div>
            <QuestionDisplay
              question={currentQuestion}
              questionNumber={currentIndex + 1}
              totalQuestions={totalQuestions}
              value={answers[currentQuestion._id] ?? null}
              onChange={(value) => setAnswer(currentQuestion._id, value)}
            />
            <p className="mt-4 text-center text-caption text-[var(--color-text-tertiary)]">
              {answeredCount} of {totalQuestions} questions answered
            </p>
          </div>
        )}
      </Modal2>

      <SubmitConfirmationModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        onConfirm={handleSubmit}
        totalQuestions={totalQuestions}
        answeredCount={answeredCount}
        requiredRemaining={requiredRemaining}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default TakeSurveyModal;
